import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ProductType } from '../../../data/products.model';
import { ThumbnailFigure } from '../../atoms/ThumbnailFigure';

import { TagsDisplay } from '../../atoms/TagDisplay';
import { AddToCartButton } from '../finder/AddToCartButton';
import Block from '../../atoms/layout/Block';
import { ImageType } from '../../../data/aem.model';
import HealthWellbeingIcons from '../finder/HealthWellbeingIcons';
import { useI18N } from '../../hoc/withI18N';
import { PlantForwardIcons } from '../finder/PlantForwardIcons';
import { useSelector } from 'react-redux';
import {
    getHomepage,
    getLanguageCode,
} from '../../../selectors/generic';
import Tooltip from '../../atoms/layout/Tooltip';
import { selectStockInformationDescriptions } from '../../../selectors/my-puratos/stockInformation';
import { StockTypes } from '../../atoms/details/StockInformation';
import { useIsOverflow } from '../../../hooks/useIsOverflow';
import { PromoLabel } from '../promotions/promo-label/PromoLabel';
import { getCurrencyCode } from '../../../selectors/cart';
import { ProductsAPI } from '../../../api/products.api';

function ProductGridCard({
    item,
    fallback,
    allowedTags,
    hoverEffect,
    showReferenceImageFirst,
    size,
    isFinder,
    hideOverlay,
    showPlantForwardIcons = false,
    hideAddToCart = false,
    addEvent = undefined,
    tileData = undefined,
    position,
}) {
    const [priceData, setPriceData] = useState(null);

    const description = React.useMemo(() => {
        const content = item && item.referenceText;
        try {
            const div = document.createElement('div');
            div.innerHTML = content?.trim() || '';
            return window.jQuery(div).unwrap().unwrap().html();
        } catch (e) {
            return '';
        }
    }, [item]);

    const translate = useI18N();
    const dataLanguage = getLanguageCode();
    const currency = useSelector(getCurrencyCode);
    const homepage = useSelector(getHomepage);
    const tooltipMsg = useSelector((state) =>
        selectStockInformationDescriptions(
            state,
            tileData?.stockType || StockTypes.NONE
        )?.shortDescription
    ) || undefined;

    const descRef = useRef(null);
    const showFadeOut = useIsOverflow(descRef);

    const tags = item['cq:tags'] || item.tags;
    const title = item.referenceTitle || item.title || item.materialName;
    const materialNumbers =
        item?.materials?.map((material) => material.materialNumber) || undefined;

    const enablePlantForwardProductFinder = useSelector(
        (state) =>
            getHomepage(state)?.plantForwardConfiguration
                ?.enablePlantForwardProductFinder
    );
    const healthWellbeingConfig = useSelector(
        (state) => getHomepage(state).healthWellbeingConfiguration
    );

    const productClickData = item?.materials?.map((item) => {
        return { ...item, ...tileData, aemProdName: title };
    });

    useEffect(() => {
        const fetchPrice = async () => {
            if (tileData?.materialNumber) {
                try {
                    const price = await ProductsAPI.getProductPrice(tileData.materialNumber);
                    setPriceData(price);
                } catch (error) {
                    console.error('Error fetching price:', error);
                }
            }
        };

        fetchPrice();
    }, [tileData?.materialNumber]);

    return (
        <Block
            className={`finder-card col-xs-12 ${
                size ? 'col-sm-' + size : 'col-sm-6 col-md-4 col-lg-3'
            } p-flex p-flex-column`}
        >
            <div className="thumbnail">
                <ThumbnailFigure
                    image={
                        showReferenceImageFirst
                            ? item.fileReference ||
                              item.file ||
                              item.skuImage ||
                              item.skuImageUrl ||
                              (!item.image && !item.imageUrl ? fallback : null)
                            : item.skuImage ||
                              item.skuImageUrl ||
                              (!item.image && !item.imageUrl ? fallback : null)
                    }
                    altImage={
                        showReferenceImageFirst
                            ? item.skuImage ||
                              item.skuImageUrl ||
                              (!item.image && !item.imageUrl ? fallback : null)
                            : item.image || item.imageUrl
                    }
                    useAltImage={hoverEffect}
                    showReferenceImageFirst={showReferenceImageFirst}
                    hideOverlay={hideOverlay}
                    position={item.fileReferencePosition}
                    listPosition={position}
                    link={item.link || item.productUrl}
                    altText={title}
                    materialNumbers={materialNumbers}
                    addEvent={addEvent}
                    productClickData={productClickData}
                >
                    <HealthWellbeingIcons tags={tags} className="absolute-box" />
                    {showPlantForwardIcons &&
                        enablePlantForwardProductFinder &&
                        !healthWellbeingConfig.enableHealthWellbeing ? (
                            <PlantForwardIcons tags={tags} />
                        ) : null}
                </ThumbnailFigure>

                <div ref={descRef} className="caption card p-margin-top-none">
                    <h3 className="p-margin-top-sm p-margin-bottom-sm">
                        <a
                            href={item.link || item.productUrl}
                            onClick={() => addEvent(productClickData, position)}
                            onMouseDown={(e) =>
                                e.button === 1 && addEvent(productClickData, position)
                            }
                            className="p-color-black"
                        >
                            {title}
                        </a>
                        {item.promotion && <PromoLabel promotion={item.promotion} />}
                    </h3>
                    {tags && (
                        <TagsDisplay tags={tags} allowedTags={allowedTags} />
                    )}
                    <p
                        className="text-small p-margin-bottom-none"
                        dangerouslySetInnerHTML={{ __html: description }}
                    />
                    {showFadeOut && <div className="fadeout"></div>}
                </div>

                {(tileData?.length || tileData?.aemProductId) && (
                    <div className="caption p-padding-bottom-xs">
                        <a
                            href={item.link || item.productUrl}
                            onClick={() => addEvent(productClickData, position)}
                            onMouseDown={(e) =>
                                e.button === 1 && addEvent(productClickData, position)
                            }
                            className="finder-link__link"
                        >
                            {translate('Read more')}
                        </a>
                    </div>
                )}

                <div className="caption p-flex p-flex-align-center p-justify-space-between small finder-link">
                    {(tileData?.priceSalesUnit) ? (
                        <div className="p-flex p-flex-column">
                            <div className="p-flex p-flex-align-start p-margin-bottom-xs stock-information">
                                {(tileData?.promotionPriceSalesUnitDouble) > 0 ? (
                                    <div className="p-flex p-flex-column p-flex-justify-left">
                                    <h2
                                        className={`text-lowercase p-text-color-black-75 p-margin-none text-lt ${
                                            dataLanguage === 'es-pe' ? 'text-transform-none' : ''
                                        }`}
                                    >
                                        {(priceData?.basePrice)}
                                    </h2>
                                    <h2
                                        className={`text-lowercase p-text-promotion p-bold p-margin-none ${
                                            dataLanguage === 'es-pe' ? 'text-transform-none' : ''
                                        }`}
                                    >
                                        {(priceData?.finalBasePrice)}
                                    </h2>
                                </div>
                                ) : (
                                <h2
                                    className={`text-lowercase p-text-color-black-75 p-margin-none ${
                                        dataLanguage === 'es-pe' ? 'text-transform-none' : ''
                                    }`}
                                >
                                    {(priceData?.finalBasePrice)}
                                </h2>
                            )}
                                {homepage.enableStockAvailability &&
                                    (tileData?.stockType) &&
                                    tooltipMsg && (
                                        <Tooltip
                                            placement="auto"
                                            trigger="hover"
                                            tooltip={tooltipMsg}
                                        >
                                            <p
                                                className={`p-margin-none p-margin-left-sm stock-information-${
                                                    (tileData?.stockType)?.toLowerCase() || 'none'
                                                }`}
                                            />
                                        </Tooltip>
                                    )}
                            </div>

                            {(tileData?.promotionPriceSalesUnitDouble) > 0 ? (
                                <div className="p-flex">
                                    <p className="p-bold p-text-color-black-75 p-margin-none text-lt">
                                        {priceData?.salesPrice}
                                    </p>
                                    <span className="p-text-promotion p-bold p-margin-left-sm">
                                        {priceData?.finalSalesPrice}
                                    </span>
                                </div>
                            ) : (
                                <p className="p-bold p-text-color-black-75 p-margin-none">
                                    {priceData?.finalSalesPrice}
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="finder-link__text">
                            <a
                                href={item.link || item.productUrl}
                                onClick={() => addEvent(productClickData, position)}
                                onMouseDown={(e) =>
                                    e.button === 1 && addEvent(productClickData, position)
                                }
                                className="finder-link__link"
                            >
                                {translate('Read more')}
                            </a>
                        </p>
                    )}

                    {!hideAddToCart && (
                        <AddToCartButton
                            product={item}
                            isFinder={isFinder}
                            useHasPrice={false}
                        />
                    )}
                </div>
            </div>
        </Block>
    );
}

ProductGridCard.propTypes = {
    item: PropTypes.shape(ProductType),
    fallback: PropTypes.shape(ImageType),
    allowedTags: PropTypes.arrayOf(PropTypes.string),
    size: PropTypes.string,
    showReferenceImageFirst: PropTypes.bool,
    hoverEffect: PropTypes.bool,
    isFinder: PropTypes.bool,
};

ProductGridCard.defaultProps = {
    allowedTags: [],
    size: undefined,
    showReferenceImageFirst: false,
    hoverEffect: false,
    isFinder: false,
    hideOverlay: false,
};

export default ProductGridCard;